import { Injectable, InjectionToken, signal, computed, type Signal, inject, DestroyRef, effect, untracked } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router, NavigationEnd } from '@angular/router';
import { Subject, type Observable, of, catchError, debounceTime, switchMap, map, distinctUntilChanged, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { USER_ID_PROVIDER } from './user-scoped-storage.service';

/**
 * Search scope configuration derived from navigation routes.
 * Each scope corresponds to a filterable catalog/list page.
 */
export interface SearchScopeConfig {
  /** Unique identifier for the scope (matches nav item id) */
  id: string;
  /** Translation key for the label */
  labelKey: string;
  /** Material icon name */
  icon: string;
  /** Route patterns that activate this scope (empty for 'all') */
  routePatterns: RegExp[];
  /** API query parameter name for search (default: 'name') */
  searchParam?: string;
  /** Route to navigate to when this scope is selected */
  route?: string;
  /** API endpoint for fetching search results (for global search) */
  apiEndpoint?: string;
  /** Field to display as the result title */
  titleField?: string | ((item: Record<string, unknown>) => string);
  /** Field to display as the result subtitle */
  subtitleField?: string | ((item: Record<string, unknown>) => string);
}

/**
 * Event emitted when a search is triggered.
 */
export interface SearchEvent {
  /** The search query string */
  query: string;
  /** The search scope id */
  scope: string;
}

/**
 * Quick action that can be triggered from search.
 */
export interface SearchQuickAction {
  /** Unique identifier */
  id: string;
  /** Display label (already translated) */
  label: string;
  /** Material icon name */
  icon: string;
  /** Route to navigate to */
  route?: string;
  /** Optional UI-side command identifier */
  commandId?: string;
  /** Optional query parameters for navigation */
  routeQueryParams?: Record<string, string>;
  /** Keywords that match this action */
  keywords: string[];
  /** Required permission (optional) */
  permission?: string;
  /** Scopes where this action is available ('all' means available everywhere) */
  scopes: string[];
}

export interface SearchQuickActionNavigation {
  route: string;
  queryParams?: Record<string, string>;
}

/**
 * A single search result from global search.
 */
export interface SearchResult {
  /** Unique identifier */
  id: string;
  /** The scope/category this result belongs to */
  scope: string;
  /** Display title */
  title: string;
  /** Display subtitle (optional) */
  subtitle?: string;
  /** Icon name */
  icon: string;
  /** Route to navigate to */
  route: string;
  /** Optional query parameters appended to the route on navigation (array values carry repeated params) */
  routeQueryParams?: Record<string, string | string[]>;
  /** Category this result is grouped under (e.g., 'persons') */
  category?: string;
  /** Translation key for the category label */
  categoryLabelKey?: string;
  /** Original item data (empty when results come from aggregated search API) */
  data: Record<string, unknown>;
}

/**
 * API scope definition returned by GET /api/v1/search/scopes.
 */
interface ApiScopeDefinition {
  id: string;
  labelKey: string;
  icon: string;
  route: string;
  routePatterns: string[];
  searchParam?: string;
  titleField?: string;
  subtitleField?: string;
  permission: string;
  quickActions: ApiScopeQuickAction[];
}

/**
 * Quick action from the API scope definition.
 */
interface ApiScopeQuickAction {
  type: string;
  labelKey: string;
  icon?: string;
  permission?: string;
  route?: string;
  commandId?: string;
}

/**
 * Response from the search scopes API.
 */
interface SearchScopesApiResponse {
  data: ApiScopeDefinition[];
}

/**
 * A contextual action from the aggregated search API.
 */
export interface ContextualAction {
  /** Unique identifier (e.g., 'calendars:date:2026-03-01') */
  id: string;
  /** Translation key for the action label */
  labelKey: string;
  /** Interpolation parameters for the label */
  labelParams: Record<string, string>;
  /** Material icon name */
  icon: string;
  /** Route to navigate to */
  route: string;
  /** Query parameters for navigation */
  queryParams: Record<string, string>;
  /** Scope ID this action belongs to */
  scope: string;
  /** Relevance score */
  score: number;
}

/**
 * Response from the aggregated search API.
 */
interface SearchResultsApiResponse {
  data: SearchResult[];
  meta: { total: number; scopes: Record<string, number> };
  contextualActions?: ContextualAction[];
}

/**
 * Injection token for providing app-specific search scopes as fallback.
 *
 * @deprecated Scopes are now fetched from the API. This token is kept as an
 * optional fallback when the search API is unavailable.
 */
export const SEARCH_SCOPES_CONFIG = new InjectionToken<SearchScopeConfig[]>('SEARCH_SCOPES_CONFIG', {
  factory: () => [],
});

/**
 * The "all" scope - used when not on a specific catalog page.
 */
const ALL_SCOPE: SearchScopeConfig = {
  id: 'all',
  labelKey: 'search.scopes.all',
  icon: 'search',
  routePatterns: [], // Matches nothing - used as fallback
};

/**
 * Global search service for coordinating search across the application.
 *
 * Features:
 * - Fetches search scopes from `GET /api/v1/search/scopes` (permission-filtered)
 * - Uses `GET /api/v1/search?q=...` for server-side aggregated global search
 * - Auto-detects search scope based on current route
 * - Falls back to static `SEARCH_SCOPES_CONFIG` when API is unavailable
 * - Provides `catalogSearchQuery()` for catalog page filtering (unchanged)
 */
@Injectable({ providedIn: 'root' })
export class SearchService {
  private readonly router = inject(Router);
  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  private readonly fallbackScopes = inject(SEARCH_SCOPES_CONFIG, { optional: true }) ?? [];

  private readonly _query = signal('');
  private readonly _isOpen = signal(false);
  private readonly _searchSubject = new Subject<SearchEvent>();
  private readonly _globalSearchSubject = new Subject<string>();

  /** Loading state for global search */
  private readonly _globalSearchLoading = signal(false);

  /** Global search results */
  private readonly _globalSearchResults = signal<SearchResult[]>([]);

  /** Whether a global search has been performed (to distinguish "no query" from "no results") */
  private readonly _globalSearchPerformed = signal(false);

  /** Contextual actions generated by the backend from parsed query tokens */
  private readonly _contextualActions = signal<ContextualAction[]>([]);

  /**
   * The auto-detected scope from route (null means 'all').
   */
  private readonly _autoDetectedScope = signal<SearchScopeConfig | null>(null);

  /**
   * The manually selected scope (null means use auto-detected).
   */
  private readonly _manualScope = signal<SearchScopeConfig | null>(null);

  /** API-fetched scopes converted to SearchScopeConfig format */
  private readonly _apiScopes = signal<SearchScopeConfig[]>([]);
  private readonly _apiScopeDefinitions = signal<ApiScopeDefinition[]>([]);
  private readonly _scopesLoaded = signal(false);
  private readonly _commandNavigations = signal<Record<string, SearchQuickActionNavigation | null>>({});
  private _scopesFetching = false;
  private readonly userIdFn = inject(USER_ID_PROVIDER, { optional: true });
  private readonly currentUserId = computed(() => this.userIdFn?.() ?? '__anon__');

  /** The 'all' scope configuration */
  readonly allScope: SearchScopeConfig = ALL_SCOPE;

  /** All available search scopes (excluding 'all') */
  readonly scopes = computed<SearchScopeConfig[]>(() => {
    const api = this._apiScopes();
    return api.length > 0 ? api : this.fallbackScopes;
  });

  /** All scopes including 'all' for dropdown display */
  readonly allScopes = computed<SearchScopeConfig[]>(() => [ALL_SCOPE, ...this.scopes()]);

  /** Current search query */
  readonly query: Signal<string> = this._query.asReadonly();

  /**
   * Current effective scope config.
   * Priority: manual selection > auto-detected > 'all'
   */
  readonly scopeConfig = computed<SearchScopeConfig>(() => {
    const manual = this._manualScope();
    if (manual) return manual;

    const auto = this._autoDetectedScope();
    if (auto) return auto;

    return ALL_SCOPE;
  });

  /** Current scope ID */
  readonly scope = computed(() => this.scopeConfig().id);

  /** Whether the search dropdown is open */
  readonly isOpen: Signal<boolean> = this._isOpen.asReadonly();

  /** Observable for search events */
  readonly search$: Observable<SearchEvent> = this._searchSubject.asObservable();

  /** Whether the current query is non-empty */
  readonly hasQuery = computed(() => this._query().trim().length > 0);

  /** Whether we're on a catalog page (auto-detected scope, not 'all') */
  readonly isCatalogPage = computed(() => this._autoDetectedScope() !== null);

  /** Whether the current scope is a catalog scope (not 'all') */
  readonly isCatalogScope = computed(() => this.scopeConfig().id !== 'all');

  /** Whether a manual scope override is active */
  readonly hasManualScope = computed(() => this._manualScope() !== null);

  /** Whether search is available (always true now) */
  readonly hasScope = computed(() => true);

  /** Whether global search is loading */
  readonly globalSearchLoading: Signal<boolean> = this._globalSearchLoading.asReadonly();

  /** Global search results (when in 'all' scope) */
  readonly globalSearchResults: Signal<SearchResult[]> = this._globalSearchResults.asReadonly();

  /** Whether a global search has been performed (to show "no results" state) */
  readonly globalSearchPerformed: Signal<boolean> = this._globalSearchPerformed.asReadonly();

  /** Contextual actions returned by the backend for the current search query */
  readonly contextualActions: Signal<ContextualAction[]> = this._contextualActions.asReadonly();

  /**
   * The search query for catalog filtering.
   * Returns the query only when on a catalog scope, otherwise empty string.
   * Services should use this to filter their API requests.
   */
  readonly catalogSearchQuery = computed(() => {
    if (!this.isCatalogScope()) {
      return '';
    }
    return this._query().trim();
  });

  /**
   * The API search parameter name for the current scope.
   * Defaults to 'name' if not specified in scope config.
   */
  readonly searchParamName = computed(() => {
    return this.scopeConfig().searchParam ?? 'name';
  });

  /**
   * All quick actions derived from API scope definitions, permission-filtered by the backend.
   * Returns raw API quick actions with scope context for internal use.
   */
  readonly allQuickActions = computed<Array<ApiScopeQuickAction & { scopeId: string }>>(() => {
    const definitions = this._apiScopeDefinitions();
    const actions: Array<ApiScopeQuickAction & { scopeId: string }> = [];
    for (const scope of definitions) {
      for (const action of scope.quickActions) {
        actions.push({ ...action, scopeId: scope.id });
      }
    }
    return actions;
  });

  /**
   * Quick actions formatted for UI consumption (translated labels, keywords, scope associations).
   * Derived from API scope definitions — no hardcoded actions.
   */
  readonly quickActions = computed<SearchQuickAction[]>(() => {
    const raw = this.allQuickActions();
    return raw.map(action => {
      let defaultIcon = 'open_in_new';
      if (action.type === 'create') {
        defaultIcon = 'add';
      } else if (action.type === 'command') {
        defaultIcon = 'bolt';
      }
      const label = this.translate.instant(action.labelKey) as string;
      // Generate keywords from label words, scope id, and action type
      const labelWords = label
        .toLowerCase()
        .split(/\s+/)
        .filter(w => w.length > 2);
      const keywords = [
        ...new Set([
          'new',
          'add',
          'create',
          'neu',
          'hinzufügen',
          'erstellen',
          action.scopeId,
          ...labelWords,
          ...this.getAdditionalQuickActionKeywords(action),
        ]),
      ];
      return {
        id: `${action.type}-${action.scopeId}`,
        label,
        icon: action.icon ?? defaultIcon,
        route: 'route' in action ? action.route : undefined,
        commandId: 'commandId' in action ? action.commandId : undefined,
        keywords,
        permission: action.permission,
        scopes: ['all', action.scopeId],
      };
    });
  });

  setCommandNavigation(commandId: string, navigation: SearchQuickActionNavigation | null): void {
    this._commandNavigations.update(current => ({ ...current, [commandId]: navigation }));
  }

  resolveQuickActionNavigation(action: SearchQuickAction): SearchQuickActionNavigation | null {
    if (action.commandId) {
      return this._commandNavigations()[action.commandId] ?? this.getDefaultCommandNavigation(action.commandId);
    }

    if (!action.route) {
      return null;
    }

    return {
      route: action.route,
      queryParams: action.routeQueryParams,
    };
  }

  constructor() {
    // Subscribe to router navigation to detect scope from route
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.onNavigationEnd(event.urlAfterRedirects);
      }
    });

    // Initialize scope from current URL and apply any query params
    this.updateScopeFromUrl(this.router.url);
    this.applyQueryParamsFromUrl(this.router.url);

    // Set up global search pipeline with debounce
    this._globalSearchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(query => {
          if (query.trim()) {
            this._globalSearchLoading.set(true);
          } else {
            this._globalSearchResults.set([]);
            this._globalSearchLoading.set(false);
            this._globalSearchPerformed.set(false);
            this._contextualActions.set([]);
          }
        }),
        switchMap(query => {
          const trimmed = query.trim();
          if (!trimmed || trimmed.length < 2) {
            return of<SearchResult[]>([]);
          }
          return this.fetchGlobalResults(trimmed);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(results => {
        this._globalSearchResults.set(results);
        this._globalSearchLoading.set(false);
        if (this._query().trim().length >= 2) {
          this._globalSearchPerformed.set(true);
        }
      });

    // Fetch scopes from API on startup (with slight delay to allow auth to settle)
    this.fetchScopes();

    let previousUserId: string | undefined;
    effect(() => {
      const userId = this.currentUserId();
      if (previousUserId === undefined) {
        previousUserId = userId;
        return;
      }
      if (previousUserId === userId) return;
      previousUserId = userId;
      untracked(() => this.clearUserState());
    });
  }

  /**
   * Fetch search scopes from the API.
   */
  private fetchScopes(): void {
    if (this._scopesLoaded() || this._scopesFetching) return;
    this._scopesFetching = true;

    this.http
      .get<SearchScopesApiResponse>('/api/v1/search/scopes')
      .pipe(
        catchError(() => {
          this._scopesFetching = false;
          if (this.fallbackScopes.length > 0) {
            console.warn('SearchService: Failed to fetch scopes from API, using fallback static scopes');
          }
          return of<SearchScopesApiResponse | null>(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((response: SearchScopesApiResponse | null) => {
        this._scopesFetching = false;
        if (response) {
          this._apiScopeDefinitions.set(response.data);
          this._apiScopes.set(response.data.map(def => this.convertApiScopeToConfig(def)));
          this._scopesLoaded.set(true);
          this.updateScopeFromUrl(this.router.url);
        }
      });
  }

  /**
   * Convert an API scope definition to the SearchScopeConfig format.
   */
  private convertApiScopeToConfig(def: ApiScopeDefinition): SearchScopeConfig {
    return {
      id: def.id,
      labelKey: def.labelKey,
      icon: def.icon,
      routePatterns: def.routePatterns.map(p => {
        try {
          return new RegExp(p);
        } catch {
          return /^$/;
        }
      }),
      searchParam: def.searchParam,
      route: def.route,
      titleField: def.titleField,
      subtitleField: def.subtitleField,
    };
  }

  /**
   * Update the current search query.
   * Always triggers the global search API (for contextual actions and results).
   * On catalog pages without a manual scope, the global search results are hidden
   * from the dropdown — but the API call still runs so contextual actions are generated.
   */
  setQuery(query: string): void {
    this._query.set(query);
    this._globalSearchSubject.next(query);
  }

  /**
   * Set the scope manually.
   * Pass null to reset to auto-detected scope.
   */
  setScope(scopeId: string | null): void {
    if (scopeId === null || scopeId === 'auto') {
      this._manualScope.set(null);
      this.triggerSearchForCurrentScope();
      return;
    }

    if (scopeId === 'all') {
      this._manualScope.set(ALL_SCOPE);
      this.triggerSearchForCurrentScope();
      return;
    }

    const scope = this.scopes().find(s => s.id === scopeId);
    if (scope) {
      this._manualScope.set(scope);
      this._globalSearchResults.set([]);
      this._globalSearchPerformed.set(false);
      this.triggerSearchForCurrentScope();
    }
  }

  /**
   * Trigger appropriate search based on current scope.
   * Always fires the global search API so contextual actions are generated
   * regardless of whether a catalog scope is active.
   */
  private triggerSearchForCurrentScope(): void {
    const query = this._query();
    if (query.trim()) {
      this._globalSearchSubject.next(query);
    } else {
      this._globalSearchResults.set([]);
      this._globalSearchPerformed.set(false);
    }
  }

  /**
   * Select a scope without navigating.
   */
  selectScope(scopeId: string): void {
    this.setScope(scopeId);
    this.close();
  }

  /**
   * Reset to the auto-detected scope based on current URL.
   */
  resetToAutoScope(): void {
    this._manualScope.set(null);
    this.triggerSearchForCurrentScope();
  }

  /**
   * Select a scope and navigate to its route if available.
   * @deprecated Use selectScope() instead - navigation is no longer automatic
   */
  selectScopeAndNavigate(scopeId: string): void {
    this.setScope(scopeId);
    const scope = scopeId === 'all' ? ALL_SCOPE : this.scopes().find(s => s.id === scopeId);
    if (scope?.route) {
      this.router.navigate([scope.route]);
    }
    this.close();
  }

  open(): void {
    this._isOpen.set(true);
  }

  close(): void {
    this._isOpen.set(false);
  }

  toggle(): void {
    this._isOpen.update(isOpen => !isOpen);
  }

  clear(): void {
    this._query.set('');
    this._globalSearchResults.set([]);
    this._globalSearchPerformed.set(false);
    this._contextualActions.set([]);
  }

  reset(): void {
    this._query.set('');
    this._manualScope.set(null);
    this._globalSearchResults.set([]);
    this._globalSearchPerformed.set(false);
    this._contextualActions.set([]);
  }

  clearUserState(): void {
    this.reset();
    this._apiScopes.set([]);
    this._apiScopeDefinitions.set([]);
    this._scopesLoaded.set(false);
    this._scopesFetching = false;
    if (this.userIdFn && this.currentUserId() === '__anon__') return;
    this.fetchScopes();
  }

  search(): void {
    const query = this._query().trim();
    const scope = this.scope();
    if (query) {
      this._searchSubject.next({ query, scope });
    }
  }

  private onNavigationEnd(url: string): void {
    const previousAutoScope = this._autoDetectedScope();
    this.updateScopeFromUrl(url);
    const newAutoScope = this._autoDetectedScope();

    if (newAutoScope !== null && previousAutoScope?.id !== newAutoScope?.id) {
      this._manualScope.set(null);
      this._query.set('');
    }

    // Read q and scope from URL query params (for shareable search URLs)
    this.applyQueryParamsFromUrl(url);
  }

  /**
   * Detect and set the auto-detected scope based on URL.
   */
  private updateScopeFromUrl(url: string): void {
    const path = url.split('?')[0].split('#')[0];
    const allScopes = this.scopes();
    const matchedScope = allScopes.find(scope => scope.routePatterns.some(pattern => pattern.test(path)));
    this._autoDetectedScope.set(matchedScope ?? null);
  }

  /**
   * Read `q` and `scope` query parameters from the URL and apply them to search state.
   * This enables shareable search URLs like `/persons?q=John&scope=persons`.
   */
  private applyQueryParamsFromUrl(url: string): void {
    const questionMarkIndex = url.indexOf('?');
    if (questionMarkIndex === -1) return;

    const queryString = url.substring(questionMarkIndex + 1).split('#')[0];
    const params = new URLSearchParams(queryString);
    const q = params.get('q');
    const scopeParam = params.get('scope');

    if (scopeParam) {
      this.setScope(scopeParam);
    }

    if (q) {
      this._query.set(q);
    }
  }

  /**
   * Fetch global search results via the aggregated search API.
   * Uses GET /api/v1/search?q=term instead of parallel per-scope requests.
   */
  private fetchGlobalResults(query: string): Observable<SearchResult[]> {
    const currentScope = this.scopeConfig();
    const params = new URLSearchParams();
    params.set('q', query);

    if (currentScope.id !== 'all') {
      params.set('scopes', currentScope.id);
      params.set('limit', '10');
    } else {
      params.set('limit', '5');
    }

    return this.http.get<SearchResultsApiResponse>(`/api/v1/search?${params.toString()}`).pipe(
      map(response => {
        // Store contextual actions from the response
        this._contextualActions.set(response.contextualActions ?? []);
        return response.data.map(item => ({ ...item, data: {} }));
      }),
      catchError(() => {
        this._contextualActions.set([]);
        return of([] as SearchResult[]);
      })
    );
  }

  private getDefaultCommandNavigation(commandId: string): SearchQuickActionNavigation | null {
    if (commandId === 'planning.labourPool.postNeed') {
      return {
        route: '/planning/labour-pool/workflow',
        queryParams: { mode: 'post-need' },
      };
    }

    return null;
  }

  private getAdditionalQuickActionKeywords(action: ApiScopeQuickAction & { scopeId: string }): string[] {
    if (action.type === 'command' && action.commandId === 'planning.labourPool.postNeed') {
      return ['springer', 'springer anfragen', 'request springer', 'post need', 'staffing request', 'bedarf melden'];
    }

    return [];
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Legacy compatibility (kept for backward compatibility with existing code)
  // ─────────────────────────────────────────────────────────────────────────────

  /** @deprecated No longer used - results are handled by individual pages */
  readonly isLoading = signal(false).asReadonly();

  /** @deprecated No longer used - full dialog is hidden */
  readonly isFullDialogOpen = signal(false).asReadonly();

  /** @deprecated No longer used */
  openFullDialog(): void {
    // No-op
  }

  /** @deprecated No longer used */
  closeFullDialog(): void {
    // No-op
  }

  /** @deprecated No longer used */
  searchWithScope(): void {
    this.search();
  }

  /** @deprecated No longer used */
  selectResult(): void {
    // No-op
  }

  /** @deprecated No longer used */
  registerProvider(): void {
    // No-op
  }

  /** @deprecated No longer used */
  readonly results = signal<unknown[]>([]).asReadonly();
}

/**
 * @deprecated Use scopes from SearchService.scopes instead.
 * Static scopes array kept for backward compatibility.
 */
export const DEFAULT_APP_SEARCH_SCOPES: SearchScopeConfig[] = [];
